import React, { createContext, useContext, useEffect, useState } from 'react'
import { getPublicConfig } from './api'

/**
 * App-wide runtime config fetched once from GET /api/public-config, so the
 * marketing CTAs and the signup page can't drift from the backend's
 * PUBLIC_SIGNUP_ENABLED flag. Flipping the flag after beta is a backend env
 * change + restart — no rebuild — and the SPA picks it up on next load.
 *
 * Until the fetch resolves, `publicSignup` defaults to `true` (the normal
 * open-signup state). Rationale: if the config call fails or is slow, the app
 * should fail OPEN to the marketing-standard trial CTAs rather than flash an
 * "invite only" state; the backend /api/onboard gate is the real authority and
 * still rejects un-invited signups with 403 when the flag is off.
 */
const PublicConfigContext = createContext({ publicSignup: true, loading: true })

export function PublicConfigProvider({ children }) {
  const [state, setState] = useState({ publicSignup: true, loading: true })

  useEffect(() => {
    let alive = true
    getPublicConfig()
      .then(cfg => {
        if (alive) setState({ publicSignup: cfg.public_signup !== false, loading: false })
      })
      .catch(() => {
        // Fail open — see the note above.
        if (alive) setState({ publicSignup: true, loading: false })
      })
    return () => { alive = false }
  }, [])

  return (
    <PublicConfigContext.Provider value={state}>
      {children}
    </PublicConfigContext.Provider>
  )
}

/** True when public signup is open; false during the private beta. */
export function usePublicSignup() {
  return useContext(PublicConfigContext).publicSignup
}

export function usePublicConfig() {
  return useContext(PublicConfigContext)
}
